import React, { type ReactNode, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { getLikeCount, LIKE_REACTION_TYPE } from '../../../../../../../common/ui/like-count';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import SearchMetadataLink from '../../../../../../../common/ui/search-metadata-link';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import type { searchGraphComment_AtlassianSearchPage$key } from './__generated__/searchGraphComment_AtlassianSearchPage.graphql';
import { messages } from './messages';

export const SearchGraphComment = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchGraphResultProps<searchGraphComment_AtlassianSearchPage$key>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const data = useFragment(
		graphql`
			fragment searchGraphComment_AtlassianSearchPage on SearchResultGraphDocument
			@argumentDefinitions(
				includeReactionsAndCommentCount: { type: "Boolean", defaultValue: false }
			) {
				__typename
				id
				title
				url
				excerpt
				lastModifiedDate
				entity {
					... on ExternalComment {
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalSpace {
								displayName
								url
							}
						}
						reactionsV2 @include(if: $includeReactionsAndCommentCount) {
							reactionType
							total
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { id, url, title, excerpt, entity, lastModifiedDate } = data;

	const lastModifiedComponent = lastModifiedDate && (
		<LastModified lastModified={lastModifiedDate} />
	);

	const label = formatMessage(messages.resultLabel);
	const containerName = entity?.container?.displayName;
	const containerUrl = entity?.container?.url;
	const container =
		containerUrl && containerName ? (
			<SearchMetadataLink label={containerName} href={containerUrl} />
		) : (
			containerName
		);

	// only include site if DC product
	// NOTE: jira-dc results now go through JiraDcSearchResult component
	// this component can be removed once a custom ConfluenceDcSearchResult component is made
	const site =
		product === ProductKeys3P.ConfluenceDC
			? thirdPartyConfigs?.[product]?.workspaceName || null
			: null;

	const headerMetadataParts: [string, ReactNode][] = [
		['label', label],
		['site', site],
		['container', container],
		['lastModified', lastModifiedComponent],
	];

	const likeCount = useMemo(() => {
		if (!fg('rovo_fps_graph_like_comment_count')) {
			return null;
		}
		const numberOfLikes =
			entity?.reactionsV2?.find(
				(reaction) => reaction?.reactionType?.toLowerCase() === LIKE_REACTION_TYPE,
			)?.total ?? 0;
		return getLikeCount(numberOfLikes);
	}, [entity?.reactionsV2]);

	const headerMetadataSocialParts: [string, ReactNode][] = likeCount
		? [['like-count', likeCount]]
		: [];

	const enrichedTitle = enrichExcerpt(title);
	const description = excerpt && <DescriptionBody description={processExcerpt(excerpt)} />;

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const footerComponent = (
		<MentionedByLinkedResults
			productConfigs={productConfigs}
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	);

	return (
		<SearchResult
			productConfigs={productConfigs}
			{...data}
			id={id}
			product={product}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			title={enrichedTitle}
			description={description}
			headerMetadataParts={headerMetadataParts}
			headerMetadataSocialParts={headerMetadataSocialParts}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			isThirdParty
			icon={icon}
			footer={footerComponent}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
			selectedSites={selectedSites}
		/>
	);
};
